import React from 'react';
import type { IncidentFormData, WoundFormData } from '../../types';
import { IncidentHeaderFields } from '../StructuredEntryFields';
import { Field, FieldsProps, InputField, inputCls, TextAreaField } from './fieldParts';
import { VisibilityToggle } from './SmallControls';

/** Phase 18 M8: wound location, L x W x D, drainage, treatment (fields the DTO already had) */
export const WoundFields: React.FC<FieldsProps<WoundFormData> & { firstRef?: React.Ref<HTMLTextAreaElement> }> = ({
  value, onChange, errors, showRequired, isMobile, disabled, firstRef,
}) => {
  const set = (field: keyof WoundFormData) => (v: string) => onChange({ ...value, [field]: v });
  const common = { isMobile, disabled, showRequired, labelClassName: 'text-pink-700' };
  return (
    <div className="space-y-2">
      <InputField {...common} id="wound-location" label="Location" placeholder="e.g. sacrum, G-tube site"
        value={value.woundLocation} onChange={set('woundLocation')} />
      <Field id="wound-length" label="Size (L × W × D)" labelClassName="text-pink-700"
        error={errors['woundMeasurements.length'] || errors['woundMeasurements.width'] || errors['woundMeasurements.depth']}>
        <div className="grid grid-cols-4 gap-1">
          {(['length', 'width', 'depth'] as const).map((k) => (
            <input key={k} id={`wound-${k}`} aria-label={`Wound ${k}`} type="number" inputMode="decimal" min={0} step="0.1"
              disabled={disabled} placeholder={k.charAt(0).toUpperCase()}
              className={inputCls({ isMobile, disabled }, Boolean(errors[`woundMeasurements.${k}`]))}
              value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })} />
          ))}
          <select aria-label="Wound size unit" disabled={disabled} className={inputCls({ isMobile, disabled })}
            value={value.units} onChange={(e) => onChange({ ...value, units: e.target.value })}>
            <option value="cm">cm</option>
            <option value="mm">mm</option>
            <option value="in">in</option>
          </select>
        </div>
      </Field>
      <InputField {...common} id="wound-drainage" label="Drainage" placeholder="e.g. scant serous"
        value={value.drainage} onChange={set('drainage')} />
      <InputField {...common} id="wound-treatment" label="Treatment" placeholder="e.g. cleansed with NS, foam dressing"
        value={value.treatmentPerformed} onChange={set('treatmentPerformed')} />
      <TextAreaField {...common} id="wound-description" label="Description" required inputRef={firstRef} rows={isMobile ? 6 : 4}
        placeholder="Appearance, wound bed, surrounding skin, care provided..."
        value={value.woundDescription} onChange={set('woundDescription')} error={errors.woundDescription} />
    </div>
  );
};

/** Incident with M8 actions / reported to / witnesses and the M6 "Internal (staff only)" toggle */
export const IncidentFields: React.FC<FieldsProps<IncidentFormData> & { firstRef?: React.Ref<HTMLTextAreaElement> }> = ({
  value, onChange, errors, showRequired, isMobile, disabled, firstRef,
}) => {
  const set = (field: keyof IncidentFormData) => (v: string) => onChange({ ...value, [field]: v });
  const common = { isMobile, disabled, showRequired, labelClassName: 'text-red-700' };
  return (
    <div className="space-y-2">
      <IncidentHeaderFields value={value} onChange={onChange} isMobile={isMobile} disabled={disabled} />
      <TextAreaField {...common} id="incident-description" label="Description" required inputRef={firstRef} rows={isMobile ? 6 : 4}
        placeholder="Describe the incident..." value={value.description} onChange={set('description')} error={errors.description} />
      <TextAreaField {...common} id="incident-actions" label="Actions taken" rows={2}
        value={value.actionsTaken} onChange={set('actionsTaken')} />
      <TextAreaField {...common} id="incident-injury" label="Injury details" rows={2}
        value={value.injuryDetails} onChange={set('injuryDetails')} />
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <InputField {...common} id="incident-reported-to" label="Reported to" placeholder="e.g. Supervisor, MD"
          value={value.reportedTo} onChange={set('reportedTo')} />
        <InputField {...common} id="incident-witnesses" label="Witnesses (comma-separated)"
          value={value.witnesses} onChange={set('witnesses')} />
      </div>
      <VisibilityToggle id="incident-internal" disabled={disabled} internal={value.internalOnly}
        onChange={(internalOnly) => onChange({ ...value, internalOnly })} />
    </div>
  );
};
